import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Alert,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";
import { FASTAPI_BASE_URL } from "../../config";
import { useAuth } from "../../AuthContext";
import { unpublishTest } from "../../apiTests";
import StatusBadge from "./StatusBadge";
import TestStatsRow from "./TestStatsRow";
import { useTranslation } from "../../i18n/useTranslation";

function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      bg: "#0f172a",
      cardBg: "#1e293b",
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      accent: "#818cf8",
      accentHover: "#6366f1",
      accentLight: "rgba(99,102,241,0.12)",
      searchBg: "#1e293b",
      pillBg: "#1e293b",
      pillActiveBg: "#818cf8",
      pillActiveText: "#ffffff",
      pillText: "#94a3b8",
      dangerText: "#f87171",
      dangerBg: "rgba(239,68,68,0.1)",
      cardAccent: "#818cf8",
      iconBtnBg: "#0f172a",
      iconBtnBorder: "#334155",
      warningText: "#fbbf24",
      warningBg: "rgba(245,158,11,0.12)",
    };
  }
  return {
    bg: "#f8fafc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    accent: "#6366f1",
    accentHover: "#4f46e5",
    accentLight: "#eef2ff",
    searchBg: "#ffffff",
    pillBg: "#f1f5f9",
    pillActiveBg: "#6366f1",
    pillActiveText: "#ffffff",
    pillText: "#64748b",
    dangerText: "#ef4444",
    dangerBg: "rgba(239,68,68,0.06)",
    cardAccent: "#6366f1",
    iconBtnBg: "#f8fafc",
    iconBtnBorder: "#e2e8f0",
    warningText: "#d97706",
    warningBg: "rgba(245,158,11,0.08)",
  };
}

function deriveStatus(test) {
  if (test.is_published) {
    const now = new Date();
    if (test.end_time && new Date(test.end_time) < now) {
      return "closed";
    }
    return "published";
  }
  return "draft";
}

async function confirmDeleteDialog(titleText, t) {
  if (Platform.OS === "web" && typeof window !== "undefined" && window.confirm) {
    return window.confirm(titleText);
  }
  return new Promise((resolve) => {
    Alert.alert(
      t("Delete test?"),
      titleText,
      [
        { text: t("Cancel"), style: "cancel", onPress: () => resolve(false) },
        { text: t("Delete"), style: "destructive", onPress: () => resolve(true) },
      ],
      { cancelable: true }
    );
  });
}

async function confirmUnpublishDialog(titleText, t) {
  if (Platform.OS === "web" && typeof window !== "undefined" && window.confirm) {
    return window.confirm(titleText);
  }
  return new Promise((resolve) => {
    Alert.alert(
      t("Unpublish test?"),
      titleText,
      [
        { text: t("Cancel"), style: "cancel", onPress: () => resolve(false) },
        { text: t("Unpublish"), style: "destructive", onPress: () => resolve(true) },
      ],
      { cancelable: true }
    );
  });
}

export default function MyTestsTab({
  isDarkMode,
  onSwitchTab,
  onEditTest,
  onViewResults,
}) {
  const { fetchWithAuth } = useAuth();
  const { t } = useTranslation();
  const colors = getColors(isDarkMode);

  const FILTERS = [
    { key: "all", label: t("All") },
    { key: "draft", label: t("Draft") },
    { key: "published", label: t("Published") },
    { key: "closed", label: t("Closed") },
  ];

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [unpublishingId, setUnpublishingId] = useState(null);

  // Fetch tests on mount
  const loadTests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/tests/manage`);
      const data = await res.json();
      const allTests = [...(data.published || []), ...(data.unpublished || [])];
      allTests.sort((a, b) => b.id - a.id);
      setTests(allTests);
    } catch (err) {
      console.error("Error fetching tests:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    let cancelled = false;
    const doLoad = async () => {
      setLoading(true);
      try {
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/tests/manage`);
        const data = await res.json();
        const allTests = [...(data.published || []), ...(data.unpublished || [])];
        allTests.sort((a, b) => b.id - a.id);
        if (!cancelled) setTests(allTests);
      } catch (err) {
        console.error("Error fetching tests:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    doLoad();
    return () => { cancelled = true; };
  }, [fetchWithAuth]);

  // Client-side filtering
  const filteredTests = useMemo(() => {
    return tests.filter((t) => {
      const status = deriveStatus(t);
      if (activeFilter !== "all" && status !== activeFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!(t.title || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [tests, activeFilter, searchQuery]);

  // Compute stats
  const stats = useMemo(() => {
    const total = tests.length;
    const published = tests.filter((t) => deriveStatus(t) === "published").length;
    return [
      { label: t("Total Tests"), value: String(total), color: "indigo", iconName: "clipboard-text-outline" },
      { label: t("Published"), value: String(published), color: "green", iconName: "check-circle-outline" },
      { label: t("Student Attempts"), value: "--", color: "teal", iconName: "account-group-outline" },
      { label: t("Avg. Score"), value: "--", color: "amber", iconName: "star-outline" },
    ];
  }, [tests]);

  // Map stats for TestStatsRow (it uses icon as a React element in Text)
  const statsForRow = useMemo(() => {
    return stats.map((s) => ({
      ...s,
      icon: (
        <MaterialCommunityIcons
          name={s.iconName}
          size={22}
          color="currentColor"
        />
      ),
    }));
  }, [stats]);

  // Unpublish handler
  const handleUnpublish = useCallback(
    async (test) => {
      const name = (test.title || t("Untitled")).trim();
      const ok = await confirmUnpublishDialog(
        t('This will unpublish "{name}" and revert it to draft. Students will no longer be able to access it.', { name }),
        t
      );
      if (!ok) return;

      setUnpublishingId(test.id);
      try {
        await unpublishTest(test.id);
        // Update local state immutably
        setTests((prev) =>
          prev.map((t) =>
            t.id === test.id
              ? { ...t, is_published: false, published_scope: null }
              : t
          )
        );
      } catch (e) {
        const msg =
          e?.response?.data?.detail || t("Could not unpublish the test. Please try again.");
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.alert(msg);
        } else {
          Alert.alert(t("Unpublish failed"), msg);
        }
      } finally {
        setUnpublishingId(null);
      }
    },
    [t]
  );

  // Delete handler
  const handleDelete = useCallback(
    async (test) => {
      if (test.is_published) {
        const msg = t("This test is published. Please unpublish it first, then delete.");
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.alert(msg);
        } else {
          Alert.alert(t("Unpublish required"), msg);
        }
        return;
      }

      const name = (test.title || t("Untitled")).trim();
      const ok = await confirmDeleteDialog(
        t('This will permanently delete "{name}" (ID {id}).', { name, id: test.id }),
        t
      );
      if (!ok) return;

      setDeletingId(test.id);
      const prevTests = tests;
      setTests((p) => p.filter((t) => t.id !== test.id));

      try {
        await axios.delete(`${FASTAPI_BASE_URL}/tests/${test.id}`);
      } catch (e) {
        const msg =
          e?.response?.data?.detail || t("Could not delete the test. Please try again.");
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.alert(msg);
        } else {
          Alert.alert(t("Delete failed"), msg);
        }
        setTests(prevTests);
      } finally {
        setDeletingId(null);
      }
    },
    [tests, t]
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={[styles.loadingText, { color: colors.textMuted }]}>
          {t("Loading tests...")}
        </Text>
      </View>
    );
  }

  return (
    <View>
      {/* Stats row */}
      <TestStatsRow stats={statsForRow} isDarkMode={isDarkMode} />

      {/* Header: Search + Filters + Create button */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {/* Search box */}
          <View
            style={[
              styles.searchBox,
              {
                backgroundColor: colors.searchBg,
                borderColor: colors.border,
              },
            ]}
          >
            <MaterialCommunityIcons
              name="magnify"
              size={18}
              color={colors.textMuted}
            />
            <TextInput
              style={[styles.searchInput, { color: colors.textPrimary }]}
              placeholder={t("Search tests...")}
              placeholderTextColor={colors.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>

          {/* Filter pills */}
          <View style={styles.filterPills}>
            {FILTERS.map((f) => {
              const isActive = activeFilter === f.key;
              return (
                <TouchableOpacity
                  key={f.key}
                  style={[
                    styles.pill,
                    {
                      backgroundColor: isActive
                        ? colors.pillActiveBg
                        : colors.pillBg,
                    },
                  ]}
                  onPress={() => setActiveFilter(f.key)}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.pillText,
                      {
                        color: isActive
                          ? colors.pillActiveText
                          : colors.pillText,
                      },
                    ]}
                  >
                    {f.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Create New Test button */}
        <TouchableOpacity
          style={[styles.createBtn, { backgroundColor: colors.accent }]}
          onPress={() => onSwitchTab && onSwitchTab("createEdit")}
          activeOpacity={0.7}
          title={t("Create New Test")}
        >
          <MaterialCommunityIcons name="plus" size={18} color="#ffffff" />
          <Text style={styles.createBtnText}>{t("Create New Test")}</Text>
        </TouchableOpacity>
      </View>

      {/* Test cards grid */}
      {filteredTests.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons
            name="clipboard-text-outline"
            size={48}
            color={colors.textMuted}
          />
          <Text style={[styles.emptyTitle, { color: colors.textSecondary }]}>
            {searchQuery || activeFilter !== "all"
              ? t("No tests match your filters")
              : t("No tests yet")}
          </Text>
          <Text style={[styles.emptySubtitle, { color: colors.textMuted }]}>
            {searchQuery || activeFilter !== "all"
              ? t("Try adjusting your search or filters")
              : t("Create your first test to get started")}
          </Text>
        </View>
      ) : (
        <View style={styles.grid}>
          {filteredTests.map((test) => {
            const status = deriveStatus(test);
            const questionCount = test.chapters
              ? Array.isArray(test.chapters)
                ? test.chapters.length
                : 0
              : 0;

            return (
              <View
                key={test.id}
                style={[
                  styles.card,
                  {
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  },
                ]}
              >
                {/* Accent top bar */}
                <View
                  style={[
                    styles.cardAccentBar,
                    { backgroundColor: colors.cardAccent },
                  ]}
                />

                {/* Card top: Title + badge */}
                <View style={styles.cardTop}>
                  <Text
                    style={[styles.cardTitle, { color: colors.textPrimary }]}
                    numberOfLines={2}
                  >
                    {test.title || t("Untitled Test")}
                  </Text>
                  <StatusBadge status={status} isDarkMode={isDarkMode} />
                </View>

                {/* Card meta row */}
                <View style={styles.cardMeta}>
                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name="help-circle-outline"
                      size={14}
                      color={colors.textMuted}
                    />
                    <Text style={[styles.metaText, { color: colors.textSecondary }]}>
                      {questionCount > 0
                        ? `${questionCount} ${t("topics")}`
                        : t("No topics")}
                    </Text>
                  </View>
                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name="clock-outline"
                      size={14}
                      color={colors.textMuted}
                    />
                    <Text style={[styles.metaText, { color: colors.textSecondary }]}>
                      {test.duration_minutes
                        ? `${test.duration_minutes} min`
                        : t("No limit")}
                    </Text>
                  </View>
                </View>

                {/* Card bottom: date + actions */}
                <View style={[styles.cardBottom, { borderTopColor: colors.border }]}>
                  <Text style={[styles.cardDate, { color: colors.textMuted }]}>
                    {formatDate(test.created_at)}
                  </Text>
                  <View style={styles.cardActions}>
                    {/* Edit */}
                    {status !== "closed" && (
                      <TouchableOpacity
                        style={[
                          styles.iconBtn,
                          {
                            backgroundColor: colors.iconBtnBg,
                            borderColor: colors.iconBtnBorder,
                          },
                        ]}
                        onPress={() => onEditTest && onEditTest(test.id)}
                        activeOpacity={0.7}
                        title={t("Edit test")}
                      >
                        <MaterialCommunityIcons
                          name="pencil-outline"
                          size={14}
                          color={colors.textSecondary}
                        />
                      </TouchableOpacity>
                    )}

                    {/* Preview */}
                    <TouchableOpacity
                      style={[
                        styles.iconBtn,
                        {
                          backgroundColor: colors.iconBtnBg,
                          borderColor: colors.iconBtnBorder,
                        },
                      ]}
                      onPress={() => onEditTest && onEditTest(test.id)}
                      activeOpacity={0.7}
                      title={t("Preview test")}
                    >
                      <MaterialCommunityIcons
                        name="eye-outline"
                        size={14}
                        color={colors.textSecondary}
                      />
                    </TouchableOpacity>

                    {/* Results */}
                    {status !== "draft" && (
                      <TouchableOpacity
                        style={[
                          styles.iconBtn,
                          {
                            backgroundColor: colors.iconBtnBg,
                            borderColor: colors.iconBtnBorder,
                          },
                        ]}
                        onPress={() => onViewResults && onViewResults(test.id)}
                        activeOpacity={0.7}
                        title={t("View results")}
                      >
                        <MaterialCommunityIcons
                          name="chart-bar"
                          size={14}
                          color={colors.textSecondary}
                        />
                      </TouchableOpacity>
                    )}

                    {/* Unpublish — only for published tests */}
                    {status === "published" && (
                      <TouchableOpacity
                        style={[
                          styles.iconBtn,
                          {
                            backgroundColor: colors.warningBg,
                            borderColor: "transparent",
                          },
                        ]}
                        onPress={() => handleUnpublish(test)}
                        disabled={unpublishingId === test.id}
                        title={t("Unpublish test")}
                        activeOpacity={0.7}
                        accessibilityLabel={t("Unpublish test")}
                      >
                        {unpublishingId === test.id ? (
                          <ActivityIndicator size={14} color={colors.warningText} />
                        ) : (
                          <MaterialCommunityIcons
                            name="publish-off"
                            size={14}
                            color={colors.warningText}
                          />
                        )}
                      </TouchableOpacity>
                    )}

                    {/* Delete */}
                    {status !== "closed" && (
                      <TouchableOpacity
                        style={[
                          styles.iconBtn,
                          styles.iconBtnDanger,
                          {
                            backgroundColor: colors.dangerBg,
                            borderColor: "transparent",
                          },
                        ]}
                        onPress={() => handleDelete(test)}
                        disabled={deletingId === test.id}
                        activeOpacity={0.7}
                        title={t("Delete test")}
                      >
                        <MaterialCommunityIcons
                          name="delete-outline"
                          size={14}
                          color={colors.dangerText}
                        />
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  /* Loading */
  loadingContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 80,
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    fontWeight: "500",
  },

  /* Header */
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    flex: 1,
  },

  /* Search */
  searchBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    minWidth: 220,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    outlineStyle: "none",
  },

  /* Filter pills */
  filterPills: {
    flexDirection: "row",
    gap: 6,
  },
  pill: {
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderRadius: 20,
  },
  pillText: {
    fontSize: 13,
    fontWeight: "600",
  },

  /* Create button */
  createBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 11,
    paddingHorizontal: 20,
    borderRadius: 10,
  },
  createBtnText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "700",
  },

  /* Empty state */
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 64,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 8,
  },
  emptySubtitle: {
    fontSize: 13,
  },

  /* Grid */
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
  },

  /* Card */
  card: {
    width: 340,
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },
  cardAccentBar: {
    height: 3,
    width: "100%",
  },
  cardTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 20,
    paddingBottom: 0,
    gap: 10,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: "700",
    flex: 1,
    lineHeight: 22,
  },

  /* Card meta */
  cardMeta: {
    flexDirection: "row",
    gap: 16,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 16,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  metaText: {
    fontSize: 12,
    fontWeight: "500",
  },

  /* Card bottom */
  cardBottom: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: "transparent",
  },
  cardDate: {
    fontSize: 12,
    fontWeight: "500",
  },
  cardActions: {
    flexDirection: "row",
    gap: 6,
  },

  /* Icon buttons */
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnDanger: {
    borderWidth: 0,
  },
});
